import type { SupabaseClient } from '@supabase/supabase-js';
import { getEffectiveSnapshotDate } from '@/lib/supabase/snapshotConfig';
import { getTenantIdFromSession, getTenantZipCodes } from '@/lib/supabase/tenantScoping';

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface RecallQueryFilters {
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  dealer?: string;
  city?: string;
  region?: string;
  zip?: string;
  zipRadius?: number;
  campaign?: string; // Will use array extraction after Phase 5
}

export interface RecallQueryOptions {
  bounds?: MapBounds;
  filters?: RecallQueryFilters;
  ordering?: Array<{ column: string; ascending: boolean }>;
  range?: { start: number; end: number };
  userId?: string;
  mode: 'viewport' | 'search' | 'hybrid';
  excludedDealers?: string[];
  columns?: string[];
  includeCount?: boolean;
  useExtractedView?: boolean; // Use cars_with_recalls_extracted_mv for campaign filtering
  tenantId?: string; // Optional tenant ID for tenant scoping (will be extracted from session if not provided)
}

/**
 * Build a Supabase query for recalls with comprehensive filter support
 *
 * This is the centralized query builder used by both Recall List and Simple Recall Map pages.
 * It applies filters server-side for optimal performance.
 *
 * @param supabase - Supabase client instance
 * @param options - Query configuration options
 * @returns Configured Supabase query builder
 */
export async function buildRecallQuery(
  supabase: SupabaseClient<any, 'public', any>,
  options: RecallQueryOptions
) {
  const {
    bounds,
    filters = {},
    ordering = [
      { column: 'service_provider_name', ascending: true },
      { column: 'vin', ascending: true }
    ],
    range,
    excludedDealers = [],
    columns = '*',
    includeCount = false,
    useExtractedView = false
  } = options;

  // Get tenant ID for tenant scoping
  let tenantId = options.tenantId;
  if (!tenantId) {
    tenantId = await getTenantIdFromSession(supabase);
  }

  // Determine which table/view to query
  const tableName =
    useExtractedView && filters.campaign?.trim()
      ? 'cars_with_recalls_extracted_mv'
      : 'cars_with_safety_recalls_mv';

  // CRITICAL: Since we revoked SELECT on cars_with_safety_recalls_mv, we cannot query it directly
  // Throw error BEFORE creating any query object to prevent Supabase from sending HEAD requests
  if (tableName === 'cars_with_safety_recalls_mv') {
    // Block ALL direct queries to the materialized view, not just count queries
    // This prevents HEAD requests that cause 403 errors and infinite loops
    throw new Error(
      'DIRECT_QUERY_NOT_ALLOWED: Direct queries to cars_with_safety_recalls_mv are not allowed. Use getRecallsForTenant() or getRecallsForTenantWithFilters() RPC functions instead.'
    );
  }

  // Start with base query (only for other tables/views)
  const queryOptions: { count?: 'exact' } = {};
  if (includeCount) {
    queryOptions.count = 'exact';
  }
  let query = supabase.from(tableName).select(columns, queryOptions);

  // Apply tenant scoping if tenant ID is available
  // Note: Since we revoked direct SELECT on cars_with_safety_recalls_mv,
  // queries to it will fail with 403. The calling code should use RPC functions instead.
  // However, we'll still apply tenant filtering here for cases where SELECT is still allowed
  // (e.g., during migration period or for other views)
  if (tenantId && tableName === 'cars_with_safety_recalls_mv') {
    try {
      const tenantZipCodes = await getTenantZipCodes(supabase, tenantId);
      if (tenantZipCodes.length > 0) {
        // Filter by tenant zip codes
        query = query.in('seller_postal_code', tenantZipCodes);
      } else {
        // If tenant has no zip codes, return empty result
        query = query.eq('seller_postal_code', '__NO_ZIP_CODES__');
      }
    } catch (error) {
      console.error('Error applying tenant scoping:', error);
      // Continue without tenant filtering if there's an error
      // This ensures the app doesn't break if tenant scoping fails
    }
  }

  // Apply configured snapshot date filter if one is set
  const effectiveDate = await getEffectiveSnapshotDate();
  if (effectiveDate) {
    // Use date range to handle timestamp format in database
    const dateStart = `${effectiveDate}T00:00:00.000Z`;
    const dateEnd = `${effectiveDate}T23:59:59.999Z`;
    query = query.gte('snapshot_date', dateStart).lte('snapshot_date', dateEnd);
  }

  // Apply viewport bounds based on mode
  if (bounds) {
    const shouldApplyBounds = options.mode === 'viewport' || options.mode === 'hybrid';
    if (shouldApplyBounds) {
      query = query
        .gte('seller_latitude', bounds.south)
        .lte('seller_latitude', bounds.north)
        .gte('seller_longitude', bounds.west)
        .lte('seller_longitude', bounds.east);
    }
  }

  // Apply text filters
  if (filters.vin?.trim()) {
    query = query.ilike('vin', `%${filters.vin.trim()}%`);
  }
  if (filters.make?.trim()) {
    query = query.ilike('make_name', `%${filters.make.trim()}%`);
  }
  if (filters.model?.trim()) {
    query = query.ilike('model_name', `%${filters.model.trim()}%`);
  }
  if (filters.year) {
    query = query.eq('car_year', filters.year);
  }
  if (filters.dealer?.trim()) {
    query = query.ilike('service_provider_name', `%${filters.dealer.trim()}%`);
  }
  if (filters.city?.trim()) {
    query = query.ilike('seller_city', `%${filters.city.trim()}%`);
  }
  if (filters.region?.trim()) {
    query = query.ilike('seller_region', `%${filters.region.trim()}%`);
  }
  if (filters.zip?.trim()) {
    query = query.ilike('seller_postal_code', `%${filters.zip.trim()}%`);
  }

  // Campaign filter for extracted view
  if (filters.campaign?.trim() && tableName === 'cars_with_recalls_extracted_mv') {
    try {
      query = (query as any).contains('campaign_numbers', [filters.campaign.trim()]);
    } catch {
      // Fall back to client-side filtering
    }
  }

  // Apply dealer exclusions
  if (excludedDealers.length > 0) {
    excludedDealers.forEach(dealerName => {
      query = query.not('service_provider_name', 'eq', dealerName);
    });
  }

  // Apply ordering
  if (ordering.length > 0 && ordering[0]) {
    query = query.order(ordering[0].column, { ascending: ordering[0].ascending });
  }

  // Apply range if specified
  if (range) {
    query = query.range(range.start, range.end);
  }

  return query;
}

/**
 * Get excluded dealers for a user
 * Helper function to extract dealer exclusion logic
 */
export async function getExcludedDealers(
  supabase: SupabaseClient,
  userId: string
): Promise<string[]> {
  const { data: preferences } = await supabase
    .from('user_preferences')
    .select('entity_id, preference_value')
    .eq('user_id', userId)
    .eq('preference_type', 'dealer');

  if (!preferences || preferences.length === 0) {
    return [];
  }

  const excludedDealers = preferences
    .filter(p => {
      const pref = p.preference_value;
      if (typeof pref === 'object' && pref !== null && 'is_included' in pref) {
        return pref.is_included === false;
      }
      return pref === false;
    })
    .map(p => p.entity_id);

  return excludedDealers;
}

/**
 * Determine query mode based on active filters
 */
export function determineQueryMode(filters: RecallQueryFilters): 'viewport' | 'search' | 'hybrid' {
  const hasTextFilters =
    Boolean(filters.vin?.trim()) ||
    Boolean(filters.make?.trim()) ||
    Boolean(filters.model?.trim()) ||
    Boolean(filters.year) ||
    Boolean(filters.dealer?.trim()) ||
    Boolean(filters.city?.trim()) ||
    Boolean(filters.region?.trim()) ||
    Boolean(filters.zip?.trim());

  if (hasTextFilters) {
    // For now, use 'search' mode when text filters are active
    // In Phase 4, we'll add 'hybrid' mode to combine text filters + viewport
    return 'search';
  }

  return 'viewport';
}
